/****************************************************
 * Module Name:  stoneWiseBidPreview.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { Box, Tooltip, Typography, ThemeProvider, createTheme, Stack, Button, Badge } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import { LoadingButton } from "@mui/lab";
import Constants from "../../../utility/constants";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import renderCellExpand from "../../common/dataGridCellExpand";
import TenderStoneDetailPreviewModal from "./tenderStoneDetailPreviewModal";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import CommonSnackbar from "../../common/commonSnackBar";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IoIosAddCircleOutline } from "react-icons/io";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import ViewImageModal from "./viewImageModal";
import AddTenderStoneRemarkModal from "./addTenderStoneRemarkModal";
import UploadTenderImagesModal from "./uploadTenderImagesModal";
import logo from '../../../images/pansuriya.jpg'
import { Modal } from "react-bootstrap";
import PageTitle from "../../common/pageTitle";

const StoneWiseBidPreview = () => {
    const [companyTenderOptions, setCompanyTenderOptions] = useState([]);
    const [companyTender, setCompanyTender] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [salesBidLoader, setSalesBidLoader] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [outerLoader, setOuterLoader] = useState();
    const [outerRows, setOuterRows] = useState([]);

    const [showTenderStoneDetailModal, setShowTenderStoneDetailModal] = useState(false);
    const [tenderStoneId, setTenderStoneId] = useState("");
    const [filterValues, setFilterValues] = useState({});
    const [CompanyDataLoader, setCompanyDataLoader] = useState(true);
    const [remark, setRemark] = useState("");
    const [showUploadImgsModal, setShowUploadImgsModal] = useState(false);
    const [showAddRemarkModal, setShowAddRemarkModal] = useState(false);
    const [showViewImageModal, setShowViewImageModal] = useState(false);
    const [loader, setLoader] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_STONE);

    const [lotData, setLotData] = React.useState({});
    const [showStoneDataModal, setShowStoneDataModal] = React.useState(false); // to show data after view button clicked

    useEffect(() => {
        PageTitle("TENDER_OPERATION_BID_PREVIEW_STONE");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (Utility.isReadAllowed(local_Permission)) {
            getCompanyTender();
        }
    }

    const getCompanyTender = () => {

        setOuterRows([]);
        setCompanyDataLoader(true);
        setCompanyTenderOptions([]);
        setCompanyTender("");
        let optionsArray = [];
        TenderLotProxy.GetCompanyTendersForManufacturingAndPreview(true, "IN_PROGRESS", "IN_PROGRESS", "COMPLETED", (response) => {
            response.map((data) => {
                let option = { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                optionsArray.push(option);
                return true;
            });
            setCompanyDataLoader(false);
        })
        setCompanyTenderOptions(optionsArray);
    }

    const handleSearch = () => {
        setSalesBidLoader(true);
        setOuterRows([]);
        // need to fetch such companies and tenders which are not yet locked 
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"

        TenderLotProxy.TenderLotDataManufacturingPreview(companyTender.value, companyTender.companyid, true, "IN_PROGRESS", "IN_PROGRESS", "COMPLETED", (response) => {
            if (response && Array.isArray(response)) {
                const uniqueFilterValues = {
                    tendername: Utility.getDistinctOptions(response, "tendername"),
                    minename: Utility.getDistinctOptions(response, "minename"),
                };

                prepareColumns(uniqueFilterValues);
                setFilterValues(uniqueFilterValues);
                setSalesBidLoader(false);
                setOuterLoader(false);
                setOuterRows(response);
            } else {
                setSalesBidLoader(false);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const handleSave = (tenderstoneid, tenderlotid, priceplanid, planid, totalprice) => {

        let data = [{
            tenderstoneid: tenderstoneid,
            tenderlotid: tenderlotid,
            priceplanid: priceplanid,
            planid: planid,
            lockedby: Constants.loggedInUser.id,
            totalprice: totalprice,
        }]

        TenderLotBidProxy.LockStoneWiseBid(data, (response) => {
            if (response && response.status === "Success") {
                setShowTenderStoneDetailModal(false);
                setShowSnackbar(true);
                setSnackbarMessage("Locked Successfully");
                setSnackbarVariant("success");
                handleSearch();
            } else {
                setShowTenderStoneDetailModal(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        })
    }

    const addRemark = (data) => {
        setLoader(true);
        TenderLotProxy.AddRemark(data, tenderStoneId, (response) => {
            if (response) {
                setShowSnackbar(true);
                setSnackbarMessage("Remark Added Successfully");
                setSnackbarVariant("success");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
            }
        });
    }

    const handleUpload = (data, tenderstoneid) => {
        setLoader(true);
        TenderLotProxy.UploadTenderStoneImages(data, tenderstoneid, (response) => {
            if (response.status === "Success") {
                setShowSnackbar(true);
                setSnackbarMessage(response.errorMessage)
                setSnackbarVariant("success");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            }
        })
    }

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "tenderlotid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_LOTCODE];
                    break;
                case "mineid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_MINE_NAME];
                    break;
                case "tendername":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_AMOUNT
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const prepareInnerColumns = (uniqueFilterValues) => {
        const columnNames = innercolumnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "size":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_STONE_SIZE_PERCENT, Constants.COLUMN_PERMISSION.MFG_STONE_PIECES];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, innercolumnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        return columnNames;
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const columnsWithPermission = {
        "eabe76e1-e41b-40ef-b9ae-cbd13293bd1e": {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 78,
            renderCell: renderCellExpand,
            filterOperators: Utility.defaultFilters(),
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "minename",
            headerName: "MINE NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3634473f-45cf-4def-8e69-c1f34ebe0f18": {
            field: "basicprice",
            headerName: "BASIC PRICE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "394908cf-dc48-40ee-b68f-23efa651806e": {
            field: "basicamount",
            headerName: "BASIC AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
    };

    const columnsWithoutPermission = [
        {
            field: "",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: (params) => (
                <Button
                    sx={{
                        width: "44px !important",
                        height: "28px !important",
                        fontSize: "10px !important",
                        borderRadius: "12px !important",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowStoneDataModal(true);
                        setLotData(params.row)
                    }}
                    component="label"
                    variant="contained"
                    size="small"
                >
                    VIEW
                </Button >
            ),
        },
        {
            field: "tenderlotid",
            headerName: "TENDER LOT ID",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mineid",
            headerName: "MINE ID",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tenderid",
            headerName: "TENDER ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tendername",
            headerName: "TENDER",
            headerAlign: "center",
            align: "center",
            width: 212,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            headerAlign: "center",
            align: "center",
            width: 212,
            renderCell: renderCellExpand,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 200,
            renderCell: renderCellExpand,
        },
        {
            field: "pcs",
            headerName: "PCS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => (params.value).toFixed(2),
            renderCell: (params) => (params.value).toFixed(2),
        },
    ];

    const innercolumnsWithPermission = {
        "86504376-84c2-4f7c-9187-bdd52e129f79": {
            field: "stonesizepercent",
            headerName: "STONE SIZE %",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "9c0b6a70-9b92-4283-b27c-4e424c5d35ef": {
            field: "pieces",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    };

    const innercolumnsWithoutPermission = [
        {
            field: "id",
            headerName: "TENDER STONE ID",
            headerAlign: "center",
            align: "center",
            width: 138,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "planids",
            headerName: "PLAN IDS",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "view",
            headerName: "PLAN",
            headerAlign: "center",
            align: "center",
            width: 138,
            renderCell: (params) => {
                return (
                    <Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon size={20} />}
                        onClick={() => {
                            setShowTenderStoneDetailModal(true);
                            setTenderStoneId(params.row.id);
                            setShowStoneDataModal(false);
                        }}
                    >
                        VIEW PLAN
                    </Button>
                )
            },
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "addremark",
            headerName: "ADD REMARK",
            headerAlign: "center",
            align: "center",
            width: 122,
            renderCell: (params) => {
                return (
                    <Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<IoIosAddCircleOutline size={20} />}
                        onClick={() => {
                            setShowAddRemarkModal(true);
                            setTenderStoneId(params.row.id);
                            setRemark(params.row.remark)
                        }}
                    >
                        REMARK
                    </Button>
                )
            },
        },
        {
            field: "upload",
            headerName: "UPLOAD",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setTenderStoneId(params.row.id);
                            setShowUploadImgsModal(true);
                        }}
                    >
                        {" "}
                        <MdOutlineAddPhotoAlternate color="primary" size={25} />
                    </Box>
                )
            },
        },
        {
            field: "noofimages",
            headerName: "VIEW IMG",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => {
                return params.row.noofimages && params.row.noofimages > 0 ? (
                    <Badge
                        badgeContent={params.row.noofimages}
                        color="primary"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                width: "15px",
                                height: "17px",
                                borderRadius: "50%",
                            }
                        }}
                    >
                        <VideoLibraryTwoToneIcon
                            sx={{ cursor: "pointer", fontSize: "25px" }}
                            onClick={() => {
                                setShowViewImageModal(true);
                                setTenderStoneId(params.row.id);
                            }}
                        />
                    </Badge>
                ) : (
                    <span style={{ color: "black", fontStyle: "italic" }}>No Media</span>
                );
            },
        }
    ];

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row, mainData) => <DetailPanelContent row={row} mainData={mainData} />,
        [],);

    function DetailPanelContent({ row: rowProp, mainData, isModalView }) {

        let middlerows = [];
        const foundItem = mainData.find((idata) => {
            return rowProp.tenderlotid === idata.tenderlotid;
        });

        if (foundItem && foundItem.tenderstonelist) {
            middlerows = foundItem.tenderstonelist;
        }

        let sortedrows = [...middlerows];
        if (sortedrows.length > 0) {
            sortedrows = sortedrows.sort(
                (p1, p2) => (p1.id > p2.id) ? 1 : (p1.id < p2.id) ? -1 : 0);
        }

        let middlecolumns = prepareInnerColumns(filterValues);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "59vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: isModalView ? '60vh' : "auto",
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middlecolumns}
                        rows={sortedrows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: isModalView ? '90vh' : '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    const defaultMaterialTheme = createTheme();

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                {outerLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            {showSnackbar ? (
                <CommonSnackbar
                    show={true}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
            ) : null}

            {/* shows stone data of the lot which can also be accessed by "+" button */}
            {showStoneDataModal ? (
                <Modal
                    backdrop="static"
                    show={true}
                    onHide={() => setShowStoneDataModal(false)}
                    dialogClassName="max-modalSize-mdx"

                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Lot Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DetailPanelContent row={lotData} mainData={outerRows} isModalView={true} />
                    </Modal.Body>
                </Modal>)
                : null}

            {/* shows palns data of the stone which can also be accessed by "view plan" button */}
            {showTenderStoneDetailModal ? (
                <TenderStoneDetailPreviewModal
                    tenderStoneId={tenderStoneId}
                    handleSave={handleSave}
                    isStoneWise={true}
                    show={showTenderStoneDetailModal}
                    handleClose={() => setShowTenderStoneDetailModal(false)}
                />
            ) : null}

            {showUploadImgsModal ? (
                <UploadTenderImagesModal
                    show={showUploadImgsModal}
                    loader={loader}
                    handleSave={handleUpload}
                    handleClose={() => {
                        setShowUploadImgsModal(false);
                        setTenderStoneId();
                    }}
                    tenderstoneid={tenderStoneId}
                />
            ) : null}

            {showAddRemarkModal ? (
                <>
                    <AddTenderStoneRemarkModal
                        show={showAddRemarkModal}
                        remarkData={remark}
                        handleClose={() => setShowAddRemarkModal(false)}
                        handleSave={addRemark}
                    />
                </>
            ) : null}

            {showViewImageModal ? (
                <>
                    <ViewImageModal
                        show={showViewImageModal}
                        tenderstoneid={tenderStoneId}
                        handleClose={() => setShowViewImageModal(false)}
                    />
                </>
            ) : null}

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "flex-start",
                    width: "100%",
                    marginBottom: 1,
                    gap: "15px",
                    marginTop: "10px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "15px",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography sx={{ whiteSpace: "nowrap" }}>Company Tender</Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "15px",
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: "100%", sm: "25rem" },
                                minWidth: "200px",
                            }}
                        >
                            <Select menuPlacement="auto"
                                blurInputOnSelect={false}
                                menuPortalTarget={document.body}
                                styles={{
                                    ...Constants.Styles,
                                }}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                options={companyTenderOptions}
                                value={companyTender}
                                isClearable={true}
                                onChange={(e) => setCompanyTender(e)}
                            />
                        </Box>

                        {CompanyDataLoader ?
                            (<Box sx={{ marginLeft: 2 }}>
                                {<CircularProgress size={20} />}
                            </Box>) :
                            (<Box sx={{ marginLeft: 2 }}>
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => getCompanyTender()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            </Box>)}
                        <Box
                            sx={{
                                "& .Mui-disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                    color: "rgba(0, 0, 0, 0.26) !important",
                                },
                            }}
                        >
                            <LoadingButton
                                sx={{ mr: 1 }}
                                variant="contained"
                                color="info"
                                onClick={handleSearch}
                                loading={salesBidLoader}
                                loadingPosition="start"
                                startIcon={<BiSearchAlt2 />}
                                type="Submit"
                                disabled={!companyTender}
                            >
                                Search
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <ThemeProvider theme={defaultMaterialTheme}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: "70vh",
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        }
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={columnNames}
                        rows={outerRows}
                        loading={outerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        slots={{ toolbar: CustomToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        getRowId={(row) => row.tenderlotid}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    tenderlotid: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false,
                                    tenderid: false,
                                    mineid: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={outerRows.length === 0 ? null : ({ row }) => getDetailPanelContent(row, outerRows)}
                    />
                </Box>
            </ThemeProvider>

        </Box>
    )
}

export default StoneWiseBidPreview;